import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format, parseISO } from "date-fns";
import {
  CalendarDays,
  Users,
  FileText,
  Pencil,
  Trash2,
  Tag,
  Paperclip,
  Download,
  X,
  Loader2,
} from "lucide-react";
import type { CalendarEvent, Calendar } from "@/hooks/useCalendar";
import { useDeleteCalendarEvent } from "@/hooks/useCalendar";
import {
  useCalendarEventAttachments,
  useUploadCalendarAttachment,
  useDeleteCalendarAttachment,
  getAttachmentSignedUrl,
} from "@/hooks/useCalendarAttachments";
import { supabase } from "@/integrations/supabase/client";

interface CalendarEventDetailSheetProps {
  event: CalendarEvent | null;
  calendars: Calendar[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (event: CalendarEvent) => void;
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm break-all">{value}</p>
      </div>
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  planned: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  confirmed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function CalendarEventDetailSheet({
  event,
  calendars,
  open,
  onOpenChange,
  onEdit,
}: CalendarEventDetailSheetProps) {
  const [displayEvent, setDisplayEvent] = useState<CalendarEvent | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const deleteMutation = useDeleteCalendarEvent();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: attachments = [], isLoading: attachmentsLoading } =
    useCalendarEventAttachments(displayEvent?.id ?? null);
  const uploadAttachment = useUploadCalendarAttachment();
  const deleteAttachment = useDeleteCalendarAttachment();

  useEffect(() => {
    setDisplayEvent(event);
  }, [event]);

  // Resolve org_id once when the sheet opens
  useEffect(() => {
    if (!open || orgId) return;
    supabase.auth.getUser().then(async ({ data }) => {
      if (!data.user) return;
      const { data: ua } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", data.user.id)
        .single();
      setOrgId((ua as any)?.org_id ?? null);
    });
  }, [open]);

  if (!displayEvent) return null;

  const calendar = calendars.find((c) => c.id === displayEvent.calendar_id);

  const formatDateRange = () => {
    const start = parseISO(displayEvent.start_at);
    const end = parseISO(displayEvent.end_at);
    if (displayEvent.all_day) {
      return format(start, "EEEE, MMMM d, yyyy");
    }
    return `${format(start, "EEE, MMM d, yyyy h:mm a")} – ${format(end, "h:mm a")}`;
  };

  const handleDelete = () => {
    deleteMutation.mutate(displayEvent.id, {
      onSuccess: () => onOpenChange(false),
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!orgId) return;
    for (const file of files) {
      await uploadAttachment.mutateAsync({
        file,
        eventId: displayEvent.id,
        orgId,
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = async (storagePath: string, filename: string) => {
    const url = await getAttachmentSignedUrl(storagePath);
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  };

  const isOOO = calendar?.slug === "cal_ooo";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start gap-3">
            {calendar && (
              <div
                className="h-4 w-4 rounded-sm mt-1 flex-shrink-0"
                style={{ backgroundColor: calendar.color }}
              />
            )}
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-lg break-words">
                {displayEvent.title}
              </SheetTitle>
              {calendar && (
                <p className="text-sm text-muted-foreground">
                  {calendar.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={STATUS_COLORS[displayEvent.status] || ""}>
              {displayEvent.status}
            </Badge>
            {isOOO && (
              <Badge variant="secondary">Out of Office</Badge>
            )}
            {displayEvent.all_day && (
              <Badge variant="outline">All Day</Badge>
            )}
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <InfoRow
            icon={CalendarDays}
            label="Date & Time"
            value={formatDateRange()}
          />

          {displayEvent.client_or_brand && (
            <InfoRow
              icon={Tag}
              label="Client / Brand"
              value={displayEvent.client_or_brand}
            />
          )}

          {displayEvent.required_headcount != null && (
            <InfoRow
              icon={Users}
              label="Required Headcount"
              value={String(displayEvent.required_headcount)}
            />
          )}

          {displayEvent.notes && (
            <InfoRow
              icon={FileText}
              label="Notes"
              value={displayEvent.notes}
            />
          )}

          {/* ── Attachments ── */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" />
                Attachments
              </p>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadAttachment.isPending}
                >
                  {uploadAttachment.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Paperclip className="h-3 w-3 mr-1" />
                  )}
                  Attach
                </Button>
              </div>
            </div>

            {attachmentsLoading ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : attachments.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No attachments</p>
            ) : (
              <ul className="space-y-1">
                {attachments.map((att) => (
                  <li
                    key={att.id}
                    className="flex items-center justify-between text-sm bg-muted rounded px-2 py-1.5 gap-2"
                  >
                    <span className="truncate flex-1 min-w-0">
                      {att.filename}
                      {att.file_size && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({formatBytes(att.file_size)})
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() =>
                          handleDownload(att.storage_path, att.filename)
                        }
                        className="text-muted-foreground hover:text-foreground"
                        title="Download"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() =>
                          deleteAttachment.mutate({
                            attachmentId: att.id,
                            eventId: att.event_id,
                            storagePath: att.storage_path,
                          })
                        }
                        className="text-muted-foreground hover:text-destructive"
                        title="Remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(displayEvent)}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete event?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete "{displayEvent.title}" and all
                    its attachments. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
